import type { Board, Coord, DailyPuzzle, LocalDailyStats, Match, Tile, WordSort } from "~/types";
import { Game, type DailyVariant, type GameMode } from "./game.svelte";
import { wordToString } from "~/board/utils";
import { ONE_DAY } from "~/utils/dates";
import { openDB, type IDBPDatabase } from 'idb';

const initialStats = (): LocalDailyStats => ({
  lastGamePlayed: new Date(0).toISOString(),
  lastGameWon: new Date(0).toISOString(),
  gamesPlayed: 0,
  gamesWon: 0,
  currStreak: 0,
  bestStreak: 0,
  stars: [0, 0, 0, 0, 0],
});

type DailyGameDB = {
  game: { key: string; value: DailyGame };
  words: { key: string; value: Match };
  turns: { key: string; value: object };
  stats: { key: string; value: LocalDailyStats };
};

const openDailyDb = (variant: DailyVariant): Promise<IDBPDatabase<DailyGameDB>> => {
  return openDB<DailyGameDB>('daily' + variant, 2, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        Game.upgradeBase(db);
      }
      if (oldVersion < 2) {
        db.createObjectStore('stats');
      }
    },
  });
}

const migrateStatsFromLocalforage = async (db: IDBPDatabase<DailyGameDB>, variant: DailyVariant) => {
  if ((await db.count('stats')) > 0) return;

  const { default: localforage } = await import('localforage');
  const lf = localforage.createInstance({ name: 'gramjam', storeName: 'dailyStats' });

  // Data was saved under variant key; also check legacy 'daily' key for 6x6
  const stats =
    (await lf.getItem<LocalDailyStats>(variant)) ??
    (variant === '6x6' ? await lf.getItem<LocalDailyStats>('daily') : null);

  if (stats) {
    await db.put('stats', stats, 'current');
    await lf.removeItem(variant);
    if (variant === '6x6') await lf.removeItem('daily');
  }
}

export const getDailyStats = async (variant: DailyVariant): Promise<LocalDailyStats> => {
  const db = await openDailyDb(variant);
  await migrateStatsFromLocalforage(db, variant);
  return (await db.get('stats', 'current')) ?? initialStats();
}

export class DailyGame extends Game<Tile> {
  static _mode = 'daily' as const;
  variant: DailyVariant;
  key?: string;
  date: string;
  initialBoard: Board<Tile>;
  stars = $state<number>(0);
  fullClear = $state(false);
  completed = $state<boolean>();

  constructor(puzzle: DailyPuzzle) {
    super();
    this.id = puzzle.id;
    this.key = puzzle.key;
    this.date = puzzle.date;
    this.variant = puzzle.variant;
    this.initialBoard = $state.snapshot(puzzle.board);
    this.board = $state.snapshot(puzzle.board);
    this.latestWord = puzzle.latestWord;
    this.stars = puzzle.stars;
    this.turns = puzzle.turns?.length || puzzle.numTurns || 0;
    this.finalBoard = puzzle.finalBoard;
    this.fullClear = puzzle.fullClear;
    // TODO better way to handle lost?
    this.lost = puzzle.lost;
    this.completed = puzzle.completed;

    if (puzzle.variant === '5x5') {
      this.dims = [5, 5];
      this.swaps = puzzle.swaps ?? 18;
    } else {
      this.dims = [6, 6];
      this.swaps = puzzle.swaps ?? 20;
    }
  }

  get totalSwaps(): number {
    return this.variant === '5x5' ? 18 : 20;
  }

  get gaugeLevel(): number {
    return this.variant === '5x5' ? 3 : 4;
  }

  async init(suffix: DailyVariant = '6x6') {
    const db = await openDailyDb(suffix);
    this.db = db as unknown as typeof this.db;
    this.words = await db.count('words');
    this.turns = await db.count('turns');
    await migrateStatsFromLocalforage(db, suffix);
    return this;
  }

  private get statsDb(): IDBPDatabase<DailyGameDB> {
    if (!this.db) throw Error('not initialized');
    return this.db as unknown as IDBPDatabase<DailyGameDB>;
  }

  async getStats() {
    return (await this.statsDb.get('stats', 'current')) ?? initialStats();
  }

  saveStats = async () => {
    const store = await this.getStats();
    store.gamesPlayed++;
    store.stars[this.stars] = (store.stars[this.stars] ?? 0) + 1;

    if (this.stars > 0 && this.date) {
      if ((+new Date(this.date) - +new Date(store.lastGameWon)) / ONE_DAY <= 1) {
        store.currStreak += 1;
      } else {
        store.currStreak = 1;
      }
      store.bestStreak = Math.max(store.currStreak, store.bestStreak, 0);
      store.lastGameWon = this.date;
      store.gamesWon++;
    } else if (this.stars === 0 && this.date) {
      store.currStreak = 0;
    }
    store.lastGamePlayed = this.date;
    await this.statsDb.put('stats', store, 'current');
    return store;
  };

  serialize() {
    return {
      ...super.serialize(),
      key: this.key,
      date: this.date,
      variant: this.variant,
      stars: this.stars,
      fullClear: this.fullClear,
      completed: this.completed,
    };
  }

  // TODO
  static async load(puzzle: DailyPuzzle, latest?: boolean) {
    const game = await new this(puzzle).init(puzzle.variant);
    if (!game.db) throw Error('not initialized');
    const [record] = await game.db.getAll(Game.gameKey);
    if (record && record.turns > 0 && record.id === game.id) {
      // resume in-progress game
      return await new this({ variant: puzzle.variant, ...record }).init(puzzle.variant);
      // should this handle empty game?
    } else if (latest && game.completed) {
      return await game.loadLatest(puzzle);
    } else {
      return await game.loadArchive();
    }
  }

  async loadArchive() {
    await this.clear();
    if (!this.startedAt) {
      this.startedAt = Date.now();
    }
    return this;
  }

  async loadLatest(puzzle: DailyPuzzle) {
    await this.clear();
    // TODO restore # swaps here
    this.lost = true;
    this.disabled = true;
    // TODO fix flipped over tiles?
    if (this.finalBoard) {
      this.board = this.finalBoard;
      // TODO migrate to bulk make sure duration vs durationSeconds
      for await (const word of puzzle.words ?? []) {
        const s = wordToString(word);
        await this.appendWord({
          ...word,
          length: s.length,
          alpha: s,
        }, { restore: true });
      }
      await this.bulkInsertTurns((puzzle.turns ?? []));
    }
    return this;
  }

  decrementSwaps() {
    this.swaps--;
  }

  highlightWords(words: Match[]) {
    for (const word of words) {
      for (const tile of word.word) {
        if (!this.highlighted[tile.id]) {
          if (word.axis === 'col') {
            this.highlighted[tile.id] = 'daily-vertical';
          } else {
            this.highlighted[tile.id] = 'daily-horizontal';
          }
        }
      }
    }
  }

  setMarqueeText(match: Match, chain: number) {
    const ratio = match.score / match.word.length;
    if (ratio > 2.1 || chain >= 6) {
      this.marquee = '🎆 UNBELIEVABLE!';
    } else if (ratio >= 2 || chain === 5) {
      this.marquee = '🎉 INCREDIBLE!';
    } else if (
      ratio >= 1.75 ||
      chain === 4 ||
      match.swaps! === 1 ||
      match.intersection
    ) {
      this.marquee = '🎊 IMPRESSIVE!';
    } else if (ratio >= 1.5 || chain === 3 || match.swaps! <= 2) {
      this.marquee = '🥳 WOW!';
    } else if (ratio >= 1.25 || chain === 2 || match.swaps! <= 3) {
      this.marquee = '👍 NICE!';
    }
  };

  starMap = {
    '6x6': [
      [1, 0],
      [2, 5],
      [3, 10],
    ],
    '5x5': [
      [1, 0],
      [2, 6],
      [3, 12],
    ]
  }

  setStars() {
    this.stars = this.starMap[this.variant]
      .reverse()
      .find(([s, t]) => t <= this.swaps)?.[0]
      ?? 0;
    if (this.fullClear) {
      this.stars++;
    }
  }

  collapseBoard() {
    this.board = this.board.map((col) => col.filter((t) => t)).filter((col) => col.length);
  }

  isFullClear = $derived(this.board?.flat().filter((t) => t).length === 0);

  async reset() {
    await this.clear();
    this.lost = false;
    this.disabled = false;
    this.board = this.initialBoard;
    this.completed = false;
    this.stars = 3;
    this.fullClear = false;
  }

}

export class Tutorial extends DailyGame {
  static _mode = 'daily' as const;
  variant: DailyVariant = '6x6';

  starMap = {
    '6x6': [
      [1, 0],
      [2, 3],
      [3, 6],
    ],
    '5x5': [
      [1, 0],
      [2, 3],
      [3, 6],
    ],
  };

  get totalSwaps() { return 9; }
  get gaugeLevel() { return 3; }

  constructor(board: Board, mode: GameMode = 'daily') {
    super({
      id: 'tutorial',
      date: '',
      variant: '5x5',
      stars: 0,
      fullClear: false,
      completed: false,
      mode,
      board,
    });
    this.dims = [board.length, board[0].length];
    this.swaps = 9;
  }

  async save() { }
  async appendTurn(_coords: Coord[] = [], _duration?: number) { }
  async appendWord(_word: Match) { this.words++; }
  async getPage(_sort: WordSort, _offset: number, _pageSize = 10): Promise<Match[]> { return []; }
  async getAll(_index?: WordSort, _fromIndex?: number): Promise<Match[]> { return []; }

  collapseBoard() {
    this.board = this.board.map((col) => col.filter((t) => t)).filter((col) => col.length);
  }
}

